import { useTranslation } from 'react-i18next'
import { BadgeCheck, Loader2 } from 'lucide-react'
import {
  SubscriptionInterval,
  SUBSCRIPTION_INTERVAL_TRANSLATION_KEYS,
  type OfferType,
} from '@/constants/wizard'
import { SUBSCRIPTION_OFFERS } from '@/constants/wizard'

interface SubscriptionOfferCardProps {
  offerId: OfferType
  interval: SubscriptionInterval
  price?: string | number
  priceId?: string
  isSelected: boolean
  isCompleted?: boolean
  isLoading?: boolean
  onSelect: () => void
}

const TAG_STYLES = {
  orange: {
    bg: 'bg-[#FFEDD5]',
    text: 'text-primary',
  },
  blue: {
    bg: 'bg-[#DBEAFE]',
    text: 'text-[#002CAB]',
  },
  green: {
    bg: 'bg-[#D1FAE5]',
    text: 'text-[#004C2C]',
  },
} as const

export function SubscriptionOfferCard({
  offerId,
  interval,
  price: propPrice,
  isSelected,
  isCompleted = false,
  isLoading = false,
  onSelect,
}: SubscriptionOfferCardProps) {
  const { t } = useTranslation('wizard')
  const offer = SUBSCRIPTION_OFFERS.find(o => o.id === offerId)

  if (!offer) return null

  const priceLabelKey = SUBSCRIPTION_INTERVAL_TRANSLATION_KEYS[interval]
  const tagStyle = TAG_STYLES[offer.color]

  return (
    <div
      className={`relative flex flex-col rounded-lg border border-[#DEDEDE] bg-white px-5 py-6 shadow-[0px_3px_8px_0px_rgba(78,44,110,0.07)] transition-all ${
        isSelected ? 'border-primary' : ''
      }`}
    >
      {isCompleted && (
        <div className="absolute -top-3 -right-3">
          <BadgeCheck fill="currentColor" stroke="white" className="h-6 w-6 text-green-500" />
        </div>
      )}
      <div
        className={`mb-4 inline-block w-full self-center rounded-full py-2 text-center ${tagStyle.bg} ${tagStyle.text}`}
      >
        {t(offer.labelKey)}
      </div>

      <p className="mx-auto mb-0 flex min-h-10 max-w-[160px] flex-1 items-center justify-center text-center text-sm font-normal text-black">
        {t(offer.descriptionKey)}
      </p>

      <div className="mb-7 flex items-baseline justify-center gap-1">
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-[#6C677F]" />
            <span className="text-sm font-normal text-[#6C677F]">{t('pricing.loading')}</span>
          </div>
        ) : propPrice !== undefined ? (
          <>
            <span className="text-primary text-[17px] font-bold">
              {typeof propPrice === 'string' ? propPrice : propPrice.toString()}€
            </span>
            <span className="text-sm font-normal text-[#6C677F]">/ {t(priceLabelKey)}</span>
          </>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onSelect}
        disabled={isLoading}
        className={`rounded-md px-10 py-2.5 text-sm font-semibold transition-opacity ${
          isLoading
            ? 'cursor-not-allowed bg-gray-300 text-gray-500'
            : 'bg-primary text-primary-foreground cursor-pointer hover:opacity-90'
        }`}
      >
        {t('buttons.selectOffer')}
      </button>
    </div>
  )
}
